import React, { ReactElement, useEffect, useState } from "react"
import { FormattedMessage, FormattedNumber, injectIntl } from "react-intl"
import Select from "react-select"
import GlobalHelper from "../../../common/GlobalHelper"
import Checkbox from "../../../common/checkbox/checkbox.component"
import MoneyInputForm from "../../../common/form/moneyinput.component"
import { SelectOption } from "../../../common/types"
import IntlProps from "../../../state/helpers/IntlProps"
import { INTEGRATION_KEY } from "../../../state/integration/Constants"
import { ORIGINAL_METHOD_PAYMENT_TYPE, PaymentRowForm, PaymentTableForm, REFUND_TYPE } from "../../../state/invoice/Constants"
import PaymentTypeLabel from "../PaymentTypeLabel/PaymentTypeLabel"
import WarningLabel, { WarningType } from "../WarningLabel/WarningLabel"
import "./PaymentRefundTable.scss"

type OwnProps = {
    title: ReactElement,
    subtitle: ReactElement,
    refundType: REFUND_TYPE,
    leftToRefund: number,
    form: PaymentTableForm,
    refundMethods: SelectOption<string>[],
    collapsable?: boolean,
    collapsed?: boolean,
    editableAmount?: boolean,
    onSelectAll?: (selected: boolean) => void,
    onSelectRow: (id: string, selected: boolean) => void,
    onEditRowRefundAmount?: (id: string, amount: number) => void,
    onEditRowRefundMethod: (id: string, method: string) => void,
}

const PaymentRefundTable = (props: OwnProps & IntlProps) => {
    const [collapsed, setCollapsed] = useState<boolean>(!!props.collapsed)
    const [externalPayments, setExternalPayments] = useState<PaymentRowForm[]>([])

    useEffect(() => {
        setExternalPayments(props.form.rows.filter(row => row.integrationKey && row.integrationKey !== INTEGRATION_KEY.RAINFOREST))
    }, [props.form.rows])

    return (
        <div className="PaymentRefundTable">
            {props.collapsable ? (
                <a className="table-expand-anchor" onClick={() => setCollapsed(!collapsed)}>
                    <h5>
                        <i className={collapsed ? "fa fa-plus" : "fa fa-minus"} />
                        {props.title}
                    </h5>
                </a>
            ) : (
                <h5>
                    {props.title}
                </h5>
            )}
            {(!props.collapsable || !collapsed) && (
                <>
                    <p className="table-subheader">
                        {props.subtitle}
                    </p>
                    {externalPayments.length > 0 && (
                        <WarningLabel
                            paymentLabels={externalPayments.map(row => ({ id: row.paymentId, identifier: row.identifier }))}
                            message={
                                <FormattedMessage
                                    id="payment_refund_modal.warning_label.label"
                                    defaultMessage="{count, plural, one {was} other {were}} processed outside of QuoteMachine;
                                        please ensure you issue a refund in the original system, and then record the refund in QuoteMachine."
                                    values={{ count: externalPayments.length }}
                                />
                            }
                            type={WarningType.WARNING}
                        />
                    )}
                    <div className="table-container">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th scope="col" className="payment-col">
                                        <FormattedMessage
                                            id="invoice.payment_refund_table.column_header.payment"
                                            defaultMessage="Payment"
                                        />
                                    </th>
                                    <th scope="col" className="payment-method-col">
                                        <FormattedMessage
                                            id="invoice.payment_refund_table.column_header.payment_method"
                                            defaultMessage="Payment Method"
                                        />
                                    </th>
                                    <th scope="col" className="refundable-col">
                                        <FormattedMessage
                                            id="invoice.payment_refund_table.column_header.refundable_amount"
                                            defaultMessage="Refundable Amount"
                                        />
                                    </th>
                                    <th scope="col" className="refund-col">
                                        <FormattedMessage
                                            id="invoice.payment_refund_table.column_header.amount_to_refund"
                                            defaultMessage="Amount to refund"
                                        />
                                    </th>
                                    <th scope="col" className="refund-method-col">
                                        <FormattedMessage
                                            id="invoice.payment_refund_table.column_header.refund_method"
                                            defaultMessage="Refund Method"
                                        />
                                    </th>
                                    <th scope="col" className="checkbox-col">
                                        {props.onSelectAll && (
                                            <Checkbox
                                                checked={props.form.isAllChecked}
                                                onChange={props.onSelectAll}
                                            />
                                        )}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {props.form.rows.map(row => (
                                    <PaymentRow
                                        key={row.id}
                                        form={row}
                                        refundType={props.refundType}
                                        refundMethods={props.refundMethods}
                                        leftToRefund={props.leftToRefund}
                                        editableAmount={props.editableAmount}
                                        onSelect={(selected: boolean) => props.onSelectRow(row.id, selected)}
                                        onEditAmount={(amount: number) => {
                                            if (props.onEditRowRefundAmount) props.onEditRowRefundAmount(row.id, amount)
                                        }}
                                        onEditRefundMethod={(method: string) => props.onEditRowRefundMethod(row.id, method)}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    )
}

type PaymentRowProps = {
    refundType: REFUND_TYPE,
    leftToRefund: number,
    form: PaymentRowForm,
    refundMethods: SelectOption<string>[],
    editableAmount?: boolean,
    onSelect: (selected: boolean) => void,
    onEditAmount: (amount: number) => void,
    onEditRefundMethod: (method: string) => void,
}

const PaymentRow = injectIntl((props: PaymentRowProps & IntlProps) => {
    const getRefundMethods = (): SelectOption<string>[] => {
        const refundMethodsNoOriginal = props.refundMethods.filter(method => method.value !== ORIGINAL_METHOD_PAYMENT_TYPE)

        if (
            props.form.creditMemoPaymentDetails
            && props.form.creditMemoPaymentDetails.processing
            && props.form.refundableAmount < props.form.creditMemoPaymentDetails.refundableAmount
        ) {
            return refundMethodsNoOriginal
        }

        return !props.form.integrationKey || props.form.integrationKey === INTEGRATION_KEY.RAINFOREST
            ? props.refundMethods
            : refundMethodsNoOriginal
    }
    
    const {
        creditCardDetails,
        bankAccountDetails,
        paymentType,
        integrationKey
    } = props.form.creditMemoPaymentDetails || props.form

    return (
        <tr className={props.form.refundable ? "" : "disabled"}>
            <td>
                <a
                    href={`/payments/${props.form.paymentId}/edit`}
                    target="_blank"
                    rel="noreferrer"
                >{props.form.identifier}</a>
            </td>
            <td>
                <PaymentTypeLabel
                    creditCardDetails={creditCardDetails}
                    bankAccountDetails={bankAccountDetails}
                    paymentType={paymentType}
                    integrationKey={integrationKey}
                />
            </td>
            <td>
                <FormattedNumber
                    style="currency"
                    currency={props.form.currency}
                    value={props.form.refundableAmount}
                />
            </td>
            <td className={props.form.processing ? "align-right" : ""}>
                {props.editableAmount && !props.form.processing ? (
                    <MoneyInputForm
                        className='amount-input'
                        disabled={!props.form.selected || !props.onEditAmount}
                        currency={props.form.currency}
                        value={GlobalHelper.moneyFormat(props.form.refundAmount)}
                        onChange={props.onEditAmount}
                        debounced={true}
                        debounceTimeout={1000}
                    />
                ) : (
                    <FormattedNumber
                        style="currency"
                        currency={props.form.currency}
                        value={props.form.refundableAmount}
                    />
                )}
            </td>
            <td>
                <Select
                    value={props.form.refundMethod}
                    options={getRefundMethods()}
                    onChange={({ value }) => props.onEditRefundMethod(value)}
                    disabled={!props.form.selected || props.refundType === REFUND_TYPE.REFUND_ACCOUNT}
                    clearable={false}
                    required
                />
            </td>
            <td>
                {props.form.refundable && (
                    <Checkbox
                        checked={props.form.selected}
                        onChange={props.onSelect}
                        disabled={!props.form.selected && props.form.processing && props.form.refundableAmount > props.leftToRefund}
                    />
                )}
            </td>
        </tr>
    )
})

export default injectIntl(PaymentRefundTable)



// WEBPACK FOOTER //
// ./src/invoice/components/PaymentRefundTable/PaymentRefundTable.tsx